import type {
  IFilterOptions,
  IOptionsProps,
  IUseFilterProps,
} from "@fluidattacks/design";
import { useFilters } from "@fluidattacks/design";
import { useCallback, useMemo } from "react";
import { useTranslation } from "react-i18next";

import type { IMailmapRecord } from "../types";
import { useFiltersStorage } from "storage/filters";

const useMailmapFilters = ({
  dataset,
  setFilteredDataset,
}: Readonly<{
  dataset: IMailmapRecord[];
  setFilteredDataset: React.Dispatch<React.SetStateAction<IMailmapRecord[]>>;
}>): IUseFilterProps<IMailmapRecord> => {
  const { t } = useTranslation();
  const { setValues, getValues } = useFiltersStorage();
  const options = useMemo(
    (): IOptionsProps<IMailmapRecord>[] => [
      {
        filterOptions: [
          {
            key: "mailmapEntryName",
            label: t("mailmap.entryName"),
            type: "text",
          },
        ],
        label: t("mailmap.entryName"),
      },
      {
        filterOptions: [
          {
            key: "mailmapEntryEmail",
            label: t("mailmap.entryEmail"),
            type: "text",
          },
        ],
        label: t("mailmap.entryEmail"),
      },
      {
        filterOptions: [
          {
            key: "mailmapSubentryName",
            label: t("mailmap.subentryName"),
            type: "text",
          },
        ],
        label: t("mailmap.subentryName"),
      },
      {
        filterOptions: [
          {
            key: "mailmapSubentryEmail",
            label: t("mailmap.subentryEmail"),
            type: "text",
          },
        ],
        label: t("mailmap.subentryEmail"),
      },
    ],
    [t],
  );

  const handleGetValues = useCallback((): string | null => {
    return getValues("mailmap") as string | null;
  }, [getValues]);
  const handleSetValues = useCallback(
    (value: string): void => {
      void setValues("mailmap", value);
    },
    [setValues],
  );

  const handleFilterChange = useCallback(
    (newFilters: readonly IFilterOptions<IMailmapRecord>[]): void => {
      setFilteredDataset(
        dataset.filter((item): boolean =>
          newFilters.every((filter): boolean => {
            const filterValue = filter.value?.toString().toLowerCase() ?? "";
            const itemValue =
              item[filter.key as keyof IMailmapRecord]
                .toString()
                .toLowerCase() || "";

            return itemValue.includes(filterValue);
          }),
        ),
      );
    },
    [dataset, setFilteredDataset],
  );

  return useFilters({
    dataset,
    getValues: handleGetValues,
    onFiltersChange: handleFilterChange,
    options,
    setFilteredDataset,
    setValues: handleSetValues,
  });
};

export { useMailmapFilters };
